import React, { useState } from "react";

// The insurance Atlas has always stored and never shown: primary, secondary and
// tertiary, ranked, with effective dates and whatever the last 271 cached.
//
// Two things the card has to be honest about.
//
// 1. WHO OWNS THESE ROWS. A practice whose feed rewrites insurance on every sync
//    (eCW via Cobalt) owns them; typing a policy in here would be overwritten by
//    the next run. The backend refuses those writes; this card does not offer
//    them and says who does own them.
// 2. WHETHER A ROW IS REAL. A patient nobody has edited yet has no Coverage rows,
//    only the inline cache the sync fills, so the backend projects those for
//    display with `id: null`. A projected row cannot be term-dated — there is
//    nothing to term-date — so it renders read-only until the first write
//    materializes it.

function money(value) {
  if (value === null || value === undefined) return null;
  return `$${Number(value).toFixed(2)}`;
}

function percent(value) {
  if (value === null || value === undefined) return null;
  return `${Number(value).toFixed(0)}%`;
}

// "2026-01-04" as 1/4/2026, read as a calendar date rather than through Date, which
// would shift it a day for anyone west of UTC.
function shortDate(iso) {
  const [year, month, day] = String(iso).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return iso;
  return `${month}/${day}/${year}`;
}

// "1/1/2026 - Present" while the policy is in force, "2/4/2026 - 4/8/2026" once it
// has ended. A future end date is not shown: until it passes nobody knows for sure
// the policy ends then (David, 2026-10-21). Nothing at all when there are no dates.
// ISO strings compare correctly as text, so `today` needs no Date parsing.
export function dateRange(coverage, today = localToday()) {
  const from = coverage.effective_from;
  const to = coverage.effective_to;
  const ended = to && to.slice(0, 10) < today;
  if (!from) return ended ? `Until ${shortDate(to)}` : null;
  if (!ended) return `${shortDate(from)} - Present`;
  return `${shortDate(from)} - ${shortDate(to)}`;
}

// One policy's identity, shared by the current and past lists: payer and dates on
// top, then one labelled line each for the ids and the subscriber.
function CoverageDetails({ coverage }) {
  const dates = dateRange(coverage);
  return (
    <>
      <div className="coverage-top">
        <div className="coverage-payer">
          {coverage.payer_name || "Unnamed payer"}
        </div>
        {dates && <div className="coverage-dates">{dates}</div>}
      </div>
      <div className="coverage-ids">
        <span>Payer ID: {coverage.payer_id || "—"}</span>
        <span>Member ID: {coverage.member_id || "—"}</span>
        <span>Group number: {coverage.group_number || "—"}</span>
        <span>
          Subscriber: {coverage.subscriber_relationship_to_patient || "—"}
        </span>
      </div>
    </>
  );
}

function Benefits({ coverage }) {
  const items = [
    ["Copay", money(coverage.cached_copay)],
    ["Coinsurance", percent(coverage.cached_coinsurance)],
    ["Deductible", money(coverage.cached_deductible)],
    ["Out-of-pocket max", money(coverage.cached_oop_max)],
  ].filter(([, value]) => value !== null);

  if (items.length === 0) return null;

  return (
    <div className="coverage-benefits">
      {items.map(([label, value]) => (
        <div className="coverage-benefit" key={label}>
          <span className="coverage-benefit-label">{label}</span>
          <span className="coverage-benefit-value">{value}</span>
        </div>
      ))}
      {coverage.last_271_check_at && (
        <div className="coverage-benefit-note">
          Last checked {coverage.last_271_check_at.slice(0, 10)}
        </div>
      )}
    </div>
  );
}

function AddCoverageForm({ payers, busy, onCancel, onSubmit }) {
  const [form, setForm] = useState({
    payer_id: "",
    member_id: "",
    group_number: "",
    sequence: "",
    effective_from: "",
  });

  const set = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const submit = (event) => {
    event.preventDefault();
    const payload = {
      payer_id: form.payer_id,
      member_id: form.member_id.trim(),
    };
    if (form.group_number.trim())
      payload.group_number = form.group_number.trim();
    if (form.sequence) payload.sequence = Number(form.sequence);
    if (form.effective_from) payload.effective_from = form.effective_from;
    onSubmit(payload);
  };

  const ready = form.payer_id && form.member_id.trim();

  return (
    <form className="coverage-form" onSubmit={submit}>
      <div className="coverage-form-row">
        <label>
          Payer
          <select value={form.payer_id} onChange={set("payer_id")} required>
            <option value="">Select a payer…</option>
            {payers.map((payer) => (
              <option key={payer.id} value={payer.payer_id}>
                {payer.payer_name} ({payer.payer_id})
              </option>
            ))}
          </select>
        </label>
        <label>
          Rank
          <select value={form.sequence} onChange={set("sequence")}>
            <option value="">Next free</option>
            <option value="1">Primary</option>
            <option value="2">Secondary</option>
            <option value="3">Tertiary</option>
          </select>
        </label>
      </div>
      <div className="coverage-form-row">
        <label>
          Member ID
          <input value={form.member_id} onChange={set("member_id")} required />
        </label>
        <label>
          Group number
          <input value={form.group_number} onChange={set("group_number")} />
        </label>
        <label>
          Effective from
          <input
            type="date"
            value={form.effective_from}
            onChange={set("effective_from")}
          />
        </label>
      </div>
      {payers.length === 0 && (
        <p className="coverage-form-note">
          This practice has no payers configured yet. Add them under Practice
          Details first — a coverage has to point at a payer that can actually
          route a claim.
        </p>
      )}
      <div className="coverage-form-actions">
        <button
          type="button"
          className="pt-btn"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="pt-btn pt-btn-primary"
          disabled={!ready || busy}
        >
          {busy ? "Adding…" : "Add coverage"}
        </button>
      </div>
    </form>
  );
}

// The biller's own today, not UTC's. `toISOString()` would pre-fill tomorrow for
// anyone working a US evening, and the backend's own default is `date.today()`,
// so the two would disagree exactly at the edge where a term date matters.
function localToday() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function TermDateForm({ coverage, payers, busy, onCancel, onSubmit }) {
  const today = localToday();
  const [effectiveTo, setEffectiveTo] = useState(today);
  const [replace, setReplace] = useState(false);
  const [payerId, setPayerId] = useState("");
  const [memberId, setMemberId] = useState("");
  const [groupNumber, setGroupNumber] = useState("");

  const submit = (event) => {
    event.preventDefault();
    const payload = { effective_to: effectiveTo };
    if (replace) {
      payload.replacement = { payer_id: payerId, member_id: memberId.trim() };
      if (groupNumber.trim())
        payload.replacement.group_number = groupNumber.trim();
    }
    onSubmit(coverage.id, payload);
  };

  const ready = effectiveTo && (!replace || (payerId && memberId.trim()));

  return (
    <form className="coverage-form" onSubmit={submit}>
      <p className="coverage-form-note">
        The existing row is kept and end-dated, never overwritten — claims
        already sent were billed under it.
      </p>
      <div className="coverage-form-row">
        <label>
          Coverage ends
          <input
            type="date"
            value={effectiveTo}
            onChange={(e) => setEffectiveTo(e.target.value)}
            required
          />
        </label>
        <label className="coverage-checkbox">
          <input
            type="checkbox"
            checked={replace}
            onChange={(e) => setReplace(e.target.checked)}
          />
          Replace it with a new policy
        </label>
      </div>
      {replace && (
        <div className="coverage-form-row">
          <label>
            New payer
            <select
              value={payerId}
              onChange={(e) => setPayerId(e.target.value)}
              required
            >
              <option value="">Select a payer…</option>
              {payers.map((payer) => (
                <option key={payer.id} value={payer.payer_id}>
                  {payer.payer_name} ({payer.payer_id})
                </option>
              ))}
            </select>
          </label>
          <label>
            New member ID
            <input
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              required
            />
          </label>
          <label>
            New group number
            <input
              value={groupNumber}
              onChange={(e) => setGroupNumber(e.target.value)}
            />
          </label>
        </div>
      )}
      <div className="coverage-form-actions">
        <button
          type="button"
          className="pt-btn"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="pt-btn pt-btn-primary"
          disabled={!ready || busy}
        >
          {busy ? "Saving…" : replace ? "End and replace" : "End coverage"}
        </button>
      </div>
    </form>
  );
}

// A policy that has ended. Read-only by construction: it is history, and the claims
// billed under it still point at it, so there is nothing here to edit.
function PastCoverages({ coverages }) {
  const [open, setOpen] = useState(false);
  if (!coverages.length) return null;
  return (
    <div className="coverage-past">
      <button
        className="pt-btn pt-btn-quiet"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        {open ? "Hide past insurance" : `Past insurance (${coverages.length})`}
      </button>
      {open && (
        <ul className="coverage-list">
          {coverages.map((coverage) => (
            <li className="coverage-row" key={coverage.id}>
              <div className="coverage-rank">{coverage.sequence_label}</div>
              <div className="coverage-body">
                <CoverageDetails coverage={coverage} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function CoverageCard({
  coverages,
  pastCoverages = [],
  editable,
  managedBy,
  payers,
  readOnly,
  busy,
  onAdd,
  onReplace,
  onCheckEligibility,
  eligibilityResult,
  eligibilityBusy = false,
}) {
  const [adding, setAdding] = useState(false);
  const [terminating, setTerminating] = useState(null);

  const canWrite = editable && !readOnly;
  // A check costs money, so it is a click and a confirm — never automatic, never a
  // loop over a list. The wording says who is being asked and what it costs us.
  const askThenCheck = () => {
    const ok = window.confirm(
      "This checks with the payer via Stedi. It runs one check for this patient now.",
    );
    if (ok) onCheckEligibility();
  };

  return (
    <section className="pt-card">
      <div className="pt-card-head">
        <h3>Coverage</h3>
        {managedBy ? (
          <span className="pt-chip pt-chip-muted">Managed by {managedBy}</span>
        ) : (
          canWrite &&
          !adding && (
            <button className="pt-btn" onClick={() => setAdding(true)}>
              Add coverage
            </button>
          )
        )}
      </div>

      {adding && (
        <AddCoverageForm
          payers={payers}
          busy={busy}
          onCancel={() => setAdding(false)}
          onSubmit={(payload) => {
            onAdd(payload, () => setAdding(false));
          }}
        />
      )}

      {coverages.length === 0 && !adding && (
        <p className="pt-empty">
          No insurance on file.{" "}
          {canWrite ? "Add a policy to bill this patient." : ""}
        </p>
      )}

      <ul className="coverage-list">
        {coverages.map((coverage, index) => (
          <li
            className="coverage-row"
            key={coverage.id || `projected-${coverage.sequence}-${index}`}
          >
            <div className="coverage-rank">{coverage.sequence_label}</div>
            <div className="coverage-body">
              <CoverageDetails coverage={coverage} />
              <Benefits coverage={coverage} />
              {coverage.id && terminating === coverage.id && (
                <TermDateForm
                  coverage={coverage}
                  payers={payers}
                  busy={busy}
                  onCancel={() => setTerminating(null)}
                  onSubmit={(id, payload) =>
                    onReplace(id, payload, () => setTerminating(null))
                  }
                />
              )}
            </div>
            {canWrite && coverage.id && terminating !== coverage.id && (
              <button
                className="pt-btn pt-btn-quiet"
                onClick={() => setTerminating(coverage.id)}
              >
                End or replace
              </button>
            )}
          </li>
        ))}
      </ul>

      {onCheckEligibility && !readOnly && coverages.length > 0 && (
        <div className="coverage-eligibility">
          <button
            className="pt-btn"
            disabled={eligibilityBusy || busy}
            onClick={askThenCheck}
          >
            {eligibilityBusy ? "Checking…" : "Check eligibility"}
          </button>
          {eligibilityResult && (
            <span className="coverage-eligibility-result">
              {eligibilityResult.status}
              {eligibilityResult.plan_name ? ` · ${eligibilityResult.plan_name}` : ""}
            </span>
          )}
        </div>
      )}

      <PastCoverages coverages={pastCoverages} />
    </section>
  );
}
